import {
  ref, Ref, onMounted, onBeforeUnmount,
} from '@vue/composition-api';

interface UseNavbarScrollBehaviorOptions {
  scrollThreshold?: number;
  autoHideDelay?: number;
  showOnScrollUpDistance?: number;
}

interface UseNavbarScrollBehaviorReturn {
  isNavVisible: Ref<boolean>;
  isSaleBannerVisible: Ref<boolean>;
  hasScrolledBackground: Ref<boolean>;
  saleBannerRef: Ref<HTMLElement | null>;
  saleBannerHeight: Ref<number>;
}

/**
 * Composable for managing navbar scroll behavior
 * Handles visibility, background changes, and sale banner positioning based on scroll position
 */
export function useNavbarScrollBehavior(
  options: UseNavbarScrollBehaviorOptions = {},
): UseNavbarScrollBehaviorReturn {
  const {
    scrollThreshold = 150,
    autoHideDelay = 2000,
    showOnScrollUpDistance = 50,
  } = options;

  // Scroll state
  const isNavVisible = ref(true);
  const isSaleBannerVisible = ref(true);
  const hasScrolledBackground = ref(false);
  const lastScrollY = ref(0);
  const scrollStartY = ref(0); // Track where scroll direction changed
  const hideNavbarTimeout = ref<number | null>(null);
  const ticking = ref(false); // Throttle flag for requestAnimationFrame

  // Sale banner measurement
  const saleBannerRef = ref<HTMLElement | null>(null);
  const saleBannerHeight = ref(0);

  const updateScrollBehavior = () => {
    const currentScrollY = window.scrollY;

    // Clear any existing timeout
    if (hideNavbarTimeout.value) {
      clearTimeout(hideNavbarTimeout.value);
      hideNavbarTimeout.value = null;
    }

    // Sale banner visibility: only show when at the very top
    isSaleBannerVisible.value = currentScrollY === 0;

    // If we're at the top, always show the nav with transparent backgrounds
    if (currentScrollY <= scrollThreshold) {
      isNavVisible.value = true;
      hasScrolledBackground.value = false;
      scrollStartY.value = 0;
      lastScrollY.value = currentScrollY;
      ticking.value = false; // Reset ticking flag before early return
      return;
    }

    // Check scroll direction
    const scrollingDown = currentScrollY > lastScrollY.value;

    if (scrollingDown && currentScrollY > scrollThreshold) {
      // Scrolling down - hide nav and continuously update position
      isNavVisible.value = false;
      hasScrolledBackground.value = false;
      // Keep updating the scroll position while scrolling down
      scrollStartY.value = currentScrollY;
    } else if (!scrollingDown) {
      // Scrolling up - only show nav after scrolling up by the required distance
      const scrolledUpDistance = scrollStartY.value - currentScrollY;

      if (scrolledUpDistance >= showOnScrollUpDistance) {
        isNavVisible.value = true;
        hasScrolledBackground.value = true;

        // Set timeout to hide navbar after user stops scrolling
        hideNavbarTimeout.value = window.setTimeout(() => {
          if (window.scrollY > scrollThreshold) {
            isNavVisible.value = false;
            hasScrolledBackground.value = false;
          }
        }, autoHideDelay);
      }
    }

    lastScrollY.value = currentScrollY;
    ticking.value = false;
  };

  const handleScroll = () => {
    if (!ticking.value) {
      requestAnimationFrame(updateScrollBehavior);
      ticking.value = true;
    }
  };

  onMounted(() => {
    // Initialize lastScrollY with current position
    lastScrollY.value = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Calculate sale banner height
    if (saleBannerRef.value) {
      saleBannerHeight.value = saleBannerRef.value.offsetHeight;
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
    if (hideNavbarTimeout.value) {
      clearTimeout(hideNavbarTimeout.value);
    }
  });

  return {
    isNavVisible,
    isSaleBannerVisible,
    hasScrolledBackground,
    saleBannerRef,
    saleBannerHeight,
  };
}
